// Schedule overlap detection for double-booking prevention. Pure (no Vue APIs),
// safe for shared/ and unit-testable. Times are compared as absolute instants,
// so timezone offsets in the ISO strings don't matter.

function toMs(value) {
    if (!value) return null;
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
}

// Two sessions overlap when their time ranges intersect. Back-to-back sessions
// (one ends exactly when the next begins) do NOT overlap. A session missing a
// valid start or end can't be judged, so it never conflicts.
export function sessionsOverlap(a, b) {
    const aStart = toMs(a?.start_date_and_time);
    const aEnd = toMs(a?.end_date_and_time);
    const bStart = toMs(b?.start_date_and_time);
    const bEnd = toMs(b?.end_date_and_time);
    if (aStart === null || aEnd === null || bStart === null || bEnd === null) return false;
    return aStart < bEnd && aEnd > bStart;
}

// Pack a day's sessions into as few side-by-side lanes as the conflicts require
// (UNB). My Schedule previously fell back to one column PER LOCATION the moment
// any two sessions overlapped, which scattered every non-conflicting session
// into a column of its own — eight rooms meant eight columns, most holding a
// single card.
//
// Lane assignment is the standard sweep: walk sessions in start order and drop
// each into the first lane whose last card has already finished. A day with one
// conflict therefore needs exactly two lanes, and everything else stays in lane
// one, which is what QA asked for.
//
// Ordering rules come from the ticket: earlier start wins the leftmost lane;
// same start is broken alphabetically by title so the result is stable rather
// than dependent on feed order.
export function packSessionsIntoLanes(sessions) {
    const timed = (Array.isArray(sessions) ? sessions : []).filter(
        (s) => toMs(s?.start_date_and_time) !== null && toMs(s?.end_date_and_time) !== null,
    );

    const ordered = [...timed].sort((a, b) => {
        const diff = toMs(a.start_date_and_time) - toMs(b.start_date_and_time);
        if (diff !== 0) return diff;
        return String(a.title || a.name || '').localeCompare(String(b.title || b.name || ''), undefined, { sensitivity: 'base' });
    });

    const lanes = [];
    for (const session of ordered) {
        const start = toMs(session.start_date_and_time);
        // First lane already free at this instant. Back-to-back is not a
        // conflict, matching sessionsOverlap above.
        const lane = lanes.find((l) => toMs(l[l.length - 1].end_date_and_time) <= start);
        if (lane) lane.push(session);
        else lanes.push([session]);
    }
    return lanes;
}

// First already-scheduled session that overlaps `candidate`, or null. Skips any
// entry sharing the candidate's unique_id (it's the same session).
export function findScheduleConflict(candidate, scheduled) {
    if (!candidate || !Array.isArray(scheduled)) return null;
    for (const s of scheduled) {
        if (!s || s.unique_id === candidate.unique_id) continue;
        if (sessionsOverlap(candidate, s)) return s;
    }
    return null;
}

// Every conflict WITHIN an already-saved schedule, rather than "does this one
// candidate clash?" (findScheduleConflict above).
//
// The distinction is the whole point of the feature: everything until now has
// been add-time prevention, which cannot help someone whose schedule was fine
// when they built it and became conflicted afterwards — session times shifted in
// the catalog and nothing re-examined what people had already saved (Nick,
// 2026-08-20).
//
// Takes the schedule as the app already models it: catalog sessions and custom
// events normalised through toOverlapItem, both carrying unique_id and a
// start/end. Anything without a usable time is skipped rather than guessed at,
// exactly as sessionsOverlap does.
//
// Returns four views of the same finding, because the designs need different
// ones: `pairs` for "these two clash", `byId` for a single card asking what it
// clashes with, `ids` for the cheap "is this card in conflict?" test the lists
// do per row, and `groups` for the modal, which shows a whole clashing cluster
// together.
export function findScheduleConflicts(items) {
    const timed = (Array.isArray(items) ? items : []).filter(
        (s) => s && toMs(s.start_date_and_time) !== null && toMs(s.end_date_and_time) !== null,
    );
    // Sorted by start so the sweep below can stop early, and so pairs and groups
    // come out in schedule order rather than feed order.
    const ordered = [...timed].sort((a, b) => toMs(a.start_date_and_time) - toMs(b.start_date_and_time));

    const pairs = [];
    const byId = new Map();
    const note = (a, b) => {
        if (!byId.has(a.unique_id)) byId.set(a.unique_id, []);
        byId.get(a.unique_id).push(b);
    };

    for (let i = 0; i < ordered.length; i += 1) {
        const a = ordered[i];
        const aEnd = toMs(a.end_date_and_time);
        for (let j = i + 1; j < ordered.length; j += 1) {
            const b = ordered[j];
            // Ordered by start, so once one starts at or after `a` ends, so does
            // everything after it.
            if (toMs(b.start_date_and_time) >= aEnd) break;
            // The same session listed twice is not a conflict with itself.
            if (a.unique_id === b.unique_id) continue;
            if (!sessionsOverlap(a, b)) continue;
            pairs.push([a, b]);
            note(a, b);
            note(b, a);
        }
    }

    const ids = new Set(byId.keys());

    // Clusters of transitively-connected items. A cluster is NOT "everything
    // overlapping a common instant": A can clash with B and B with C while A and
    // C never touch, and the modal still has to present those three together,
    // because resolving B is what settles both.
    const groups = [];
    const placed = new Set();
    for (const item of ordered) {
        if (!ids.has(item.unique_id) || placed.has(item.unique_id)) continue;
        const group = [];
        const queue = [item];
        placed.add(item.unique_id);
        while (queue.length) {
            const current = queue.shift();
            group.push(current);
            for (const other of byId.get(current.unique_id) || []) {
                if (placed.has(other.unique_id)) continue;
                placed.add(other.unique_id);
                queue.push(other);
            }
        }
        group.sort((a, b) => toMs(a.start_date_and_time) - toMs(b.start_date_and_time));
        groups.push(group);
    }

    return { pairs, byId, ids, groups };
}
